import { useState, useEffect } from "react";
import { fetchFeedbackList, submitFeedback } from "../services/apiService";

const TYPES = [
  { id: "bug", emoji: "🐞", label: "Báo lỗi", desc: "App bị lỗi, crash, hiển thị sai", color: "#EF4444" },
  { id: "feature", emoji: "💡", label: "Tính năng mới", desc: "Ý tưởng tính năng bạn muốn có", color: "#3B82F6" },
  { id: "suggestion", emoji: "💬", label: "Góp ý", desc: "Cải thiện trải nghiệm, giao diện", color: "#10B981" },
];

const STATUS_MAP = {
  new: { label: "Mới gửi", color: "#6B7280", bg: "#6B728015" },
  seen: { label: "Đã xem", color: "#3B82F6", bg: "#3B82F615" },
  working: { label: "Đang xử lý", color: "#F59E0B", bg: "#F59E0B15" },
  done: { label: "Hoàn thành", color: "#10B981", bg: "#10B98115" },
};

export default function FeedbackView() {
  const [selectedType, setSelectedType] = useState(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeedbackList().then(data => {
      setFeedbackList(data);
      setLoading(false);
    });
  }, []);

  const handleSubmit = async () => {
    if (!selectedType || !message.trim()) return;
    setSending(true);
    const result = await submitFeedback(selectedType, message.trim());
    if (result && !result.error) {
      setSent(true);
      setFeedbackList(prev => [result, ...prev]);
      setTimeout(() => {
        setSent(false);
        setSelectedType(null);
        setMessage("");
      }, 2000);
    }
    setSending(false);
  };

  const charCount = message.length;
  const typeInfo = selectedType ? TYPES.find(t => t.id === selectedType) : null;

  return (
    <div>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <div style={{ fontSize: "11px", letterSpacing: "2px", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "4px" }}>Đóng góp</div>
        <div style={{ fontSize: "18px", fontWeight: "700", color: "var(--text-primary)" }}>Giúp app tốt hơn mỗi ngày</div>
        <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "6px" }}>Mọi ý kiến đều quý giá với mình!</div>
      </div>

      {/* Success message */}
      {sent && (
        <div style={{ background: "#10B98115", border: "1px solid #10B98144", borderRadius: "12px", padding: "16px", marginBottom: "16px", textAlign: "center" }}>
          <div style={{ fontSize: "24px", marginBottom: "4px" }}>🎉</div>
          <div style={{ fontSize: "14px", fontWeight: "600", color: "#10B981" }}>Cảm ơn bạn đã góp ý!</div>
          <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>Mình sẽ xem xét sớm nhất có thể.</div>
        </div>
      )}

      {/* Type selector */}
      {!sent && (
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "16px", padding: "20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "12px" }}>Bạn muốn gửi gì?</div>
          <div style={{ display: "flex", gap: "8px" }}>
            {TYPES.map(t => (
              <button key={t.id} onClick={() => setSelectedType(t.id)}
                style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "6px",
                  background: selectedType === t.id ? `${t.color}15` : "transparent",
                  border: `1px solid ${selectedType === t.id ? `${t.color}66` : "var(--border-primary)"}`,
                  borderRadius: "12px", padding: "12px 8px", cursor: "pointer",
                  transition: "all 0.2s",
                }}>
                <div style={{ fontSize: "24px" }}>{t.emoji}</div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: selectedType === t.id ? t.color : "var(--text-primary)" }}>{t.label}</div>
                <div style={{ fontSize: "9px", color: "var(--text-muted)", lineHeight: 1.3 }}>{t.desc}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Message input */}
      {selectedType && !sent && (
        <div style={{ background: "var(--bg-card)", border: `1px solid ${typeInfo.color}33`, borderRadius: "16px", padding: "20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: typeInfo.color, marginBottom: "12px" }}>
            {typeInfo.emoji} {typeInfo.label}
          </div>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, 2000))}
            placeholder={
              selectedType === "bug" ? "Mô tả lỗi bạn gặp: chuyện gì xảy ra, bạn đang làm gì khi gặp lỗi..."
              : selectedType === "feature" ? "Mô tả tính năng bạn muốn: nó hoạt động như thế nào, giải quyết vấn đề gì..."
              : "Chia sẻ ý kiến của bạn: điều gì có thể cải thiện, trải nghiệm nào chưa tốt..."
            }
            style={{
              width: "100%", minHeight: "120px", padding: "12px", fontSize: "13px", lineHeight: 1.6,
              background: "var(--bg-main)", border: "1px solid var(--border-primary)", borderRadius: "10px",
              color: "var(--text-primary)", resize: "vertical", outline: "none",
              fontFamily: "inherit", boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "10px" }}>
            <div style={{ fontSize: "10px", color: charCount > 1800 ? "#EF4444" : "var(--text-muted)" }}>
              {charCount}/2000
            </div>
            <button onClick={handleSubmit} disabled={!message.trim() || sending}
              style={{
                background: message.trim() && !sending ? typeInfo.color : "var(--border-primary)",
                border: "none", borderRadius: "10px", padding: "10px 24px",
                color: message.trim() && !sending ? "#fff" : "var(--text-muted)",
                fontSize: "13px", fontWeight: "700", cursor: message.trim() && !sending ? "pointer" : "not-allowed",
                transition: "all 0.2s",
              }}>
              {sending ? "Đang gửi..." : "Gửi đóng góp"}
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ marginTop: "24px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "12px" }}>
            📋 Đóng góp trước đây
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {[1, 2].map(i => (
              <div key={i} style={{ background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "12px", padding: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <div className="skeleton-pulse" style={{ width: "100px", height: "14px", borderRadius: "6px", background: "var(--border-secondary)" }} />
                  <div className="skeleton-pulse" style={{ width: "60px", height: "14px", borderRadius: "6px", background: "var(--border-secondary)" }} />
                </div>
                <div className="skeleton-pulse" style={{ width: "80%", height: "12px", borderRadius: "6px", background: "var(--border-secondary)", marginBottom: "6px" }} />
                <div className="skeleton-pulse" style={{ width: "50%", height: "12px", borderRadius: "6px", background: "var(--border-secondary)" }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Previous feedback */}
      {!loading && feedbackList.length > 0 && (
        <div style={{ marginTop: "24px" }}>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "12px" }}>
            📋 Đóng góp trước đây ({feedbackList.length})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {feedbackList.map(fb => {
              const t = TYPES.find(x => x.id === fb.type) || TYPES[2];
              const s = STATUS_MAP[fb.status] || STATUS_MAP.new;
              const date = new Date(fb.created_at);
              const dateStr = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
              return (
                <div key={fb.id} style={{ background: "var(--bg-card)", border: "1px solid var(--border-secondary)", borderRadius: "12px", padding: "14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ fontSize: "14px" }}>{t.emoji}</span>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: t.color }}>{t.label}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "10px", background: s.bg, color: s.color, padding: "2px 8px", borderRadius: "8px", fontWeight: "600" }}>
                        {s.label}
                      </span>
                      <span style={{ fontSize: "10px", color: "var(--text-muted)" }}>{dateStr}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {fb.message}
                  </div>
                  {fb.admin_reply && (
                    <div style={{ marginTop: "10px", background: "#3B82F610", border: "1px solid #3B82F633", borderRadius: "8px", padding: "10px" }}>
                      <div style={{ fontSize: "10px", fontWeight: "600", color: "#3B82F6", marginBottom: "4px" }}>💬 Phản hồi từ admin</div>
                      <div style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.5 }}>{fb.admin_reply}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && feedbackList.length === 0 && !sent && (
        <div style={{ textAlign: "center", padding: "20px", color: "var(--text-muted)", fontSize: "12px", fontStyle: "italic" }}>
          Bạn chưa gửi đóng góp nào. Mọi ý kiến đều được trân trọng!
        </div>
      )}
    </div>
  );
}
